/**
 * Salary Distribution Chart Component
 * Shows multiple salary ranges ordered by specificity (most to least specific)
 */

import React, { memo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isValidNumber, isPositiveNumber } from '../utils/numberUtils';

interface SalaryStatistics {
  median: { min: number; max: number };
  average: { min: number; max: number };
  min: number;
  max: number;
  percentile25: { min: number; max: number };
  percentile75: { min: number; max: number };
  percentile90?: { min: number; max: number };
  sampleSize: number;
  aggregationType?: string;
  dimensionValue?: string;
}

interface SalaryRange {
  stats: SalaryStatistics;
  label: string;
  color: string;
  type: 'job' | 'titleLocation' | 'companyLocation' | 'titleRoleLevel' | 'title' | 'roleLevel';
}

interface SalaryDistributionChartProps {
  salaryStats?: SalaryStatistics;
  locationSalaryStats?: Record<string, SalaryStatistics>;
  allSalaryStats?: {
    titleLocation?: Record<string, SalaryStatistics>;
    companyTitleLocation?: Record<string, SalaryStatistics>;
    title?: SalaryStatistics;
    companyTitle?: SalaryStatistics;
    locations?: Record<string, SalaryStatistics>;
    roleLevel?: SalaryStatistics;
    companyLocation?: Record<string, SalaryStatistics>;
    titleRoleLevel?: SalaryStatistics;
  };
  jobSalaryMin?: number;
  jobSalaryMax?: number;
  currency?: string;
  salaryPeriod?: 'annual' | 'monthly' | 'daily' | 'hourly';
  locale?: string;
  className?: string;
  jobLocation?: string[];
  jobTitle?: string;
  jobRoleLevel?: string;
}

export const SalaryDistributionChart: React.FC<SalaryDistributionChartProps> = memo(({
  salaryStats,
  locationSalaryStats,
  allSalaryStats,
  jobSalaryMin,
  jobSalaryMax,
  currency = '€',
  salaryPeriod = 'annual',
  locale = 'en',
  className = '',
  jobLocation = [],
  jobTitle,
  jobRoleLevel,
}) => {
  const intl = useIntl();

  // Always use EUR, regardless of input currency
  const displayCurrency = '€';

  // Helper to get sample size
  const getValidSampleSize = (stats: SalaryStatistics | undefined | null): number => {
    if (!stats) return 0;
    const size = stats.sampleSize;
    return isValidNumber(size) && size >= 0 ? size : 0;
  };

  // Helper to find stats in a record by location
  const findStatsInRecord = (record: Record<string, SalaryStatistics> | undefined, locations: string[]): { stats: SalaryStatistics; location: string } | null => {
    if (!record) return null;
    if (Object.keys(record).length === 0) return null;
    for (const location of locations) {
      const stats = record[location];
      if (stats && getValidSampleSize(stats) > 0) {
        return { stats, location };
      }
    }
    return null;
  };

  // Normalize to monthly for consistent comparison
  const normalizeToMonthly = (value: number, period: string): number => {
    if (!isValidNumber(value) || value <= 0) return 0;
    switch (period) {
      case 'annual': return value / 12;
      case 'monthly': return value;
      case 'daily': return value * 22;
      case 'hourly': return value * 22 * 8;
      default: return value;
    }
  };

  // Collect all available salary ranges in order of specificity (least to most specific)
  // We'll reverse this when rendering so most specific is at the bottom
  const collectSalaryRanges = (): SalaryRange[] => {
    const ranges: SalaryRange[] = [];

    // 1. Title only (least specific) - goes at top
    if (allSalaryStats?.title && getValidSampleSize(allSalaryStats.title) > 0) {
      ranges.push({
        stats: allSalaryStats.title,
        label: intl.formatMessage(
          { id: 'salaryChart.marketAverage', defaultMessage: 'Market Average: {title}', description: 'Chart label for market average salary by job title, where {title} is the job title' },
          { title: jobTitle || intl.formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role', description: 'Fallback text when job title is not available, used in salary chart labels' }) }
        ),
        color: '#3b82f6', // Blue
        type: 'title',
      });
    }

    // 2. Title + Role Level (titleRoleLevel)
    if (allSalaryStats?.titleRoleLevel && getValidSampleSize(allSalaryStats.titleRoleLevel) > 0) {
      ranges.push({
        stats: allSalaryStats.titleRoleLevel,
        label: intl.formatMessage(
          { id: 'salaryChart.marketAverageRoleLevel', defaultMessage: 'Market Average: {roleLevel} Level', description: 'Chart label for market average salary by role level, where {roleLevel} is the level (e.g., Junior, Senior)' },
          { roleLevel: jobRoleLevel || intl.formatMessage({ id: 'salaryChart.thisRoleLevel', defaultMessage: 'this role level', description: 'Fallback text when role level is not available, used in salary chart labels' }) }
        ),
        color: '#f97316', // Orange
        type: 'titleRoleLevel',
      });
    }

    // 3. Company + Location (companyLocation)
    if (allSalaryStats?.companyLocation && jobLocation.length > 0) {
      const found = findStatsInRecord(allSalaryStats.companyLocation, jobLocation);
      if (found && getValidSampleSize(found.stats) > 0) {
        ranges.push({
          stats: found.stats,
          label: intl.formatMessage(
            { id: 'salaryChart.companyLocation', defaultMessage: 'Company in {location}', description: 'Chart label for company salary data in specific location, where {location} is the city/region' },
            { location: found.location }
          ),
          color: '#f59e0b', // Amber
          type: 'companyLocation',
        });
      }
    }

    // 4. Title + Location (titleLocation)
    if (allSalaryStats?.titleLocation && jobLocation.length > 0) {
      const found = findStatsInRecord(allSalaryStats.titleLocation, jobLocation);
      if (found && getValidSampleSize(found.stats) > 0) {
        ranges.push({
          stats: found.stats,
          label: intl.formatMessage(
            { id: 'salaryChart.locationAverage', defaultMessage: 'In {location}: {title}', description: 'Chart label for average salary by location and title, where {location} is the city/region and {title} is the job title' },
            { 
              location: found.location,
              title: jobTitle || intl.formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role', description: 'Fallback text when job title is not available, used in salary chart labels' }) 
            }
          ),
          color: '#a855f7', // Purple
          type: 'titleLocation',
        });
      }
    }

    // 5. Job salary (most specific) - or estimate if not provided - goes at bottom
    const hasJobSalary = isPositiveNumber(jobSalaryMin) && isPositiveNumber(jobSalaryMax);
    if (hasJobSalary) {
      const jobMinMonthly = normalizeToMonthly(jobSalaryMin!, salaryPeriod);
      const jobMaxMonthly = normalizeToMonthly(jobSalaryMax!, salaryPeriod);
      
      // Create a synthetic stats object for the job range
      const jobMid = (jobMinMonthly + jobMaxMonthly) / 2;
      const jobStats: SalaryStatistics = {
        median: { min: jobMid, max: jobMid },
        average: { min: jobMinMonthly, max: jobMaxMonthly },
        min: jobMinMonthly,
        max: jobMaxMonthly,
        percentile25: { min: jobMinMonthly, max: jobMid },
        percentile75: { min: jobMid, max: jobMaxMonthly },
        sampleSize: 1,
      };
      
      // Only include roleLevel in label if it exists
      const offerLabel = jobRoleLevel
        ? intl.formatMessage(
            { id: 'salaryChart.specificOffer', defaultMessage: 'This Offer ({roleLevel})', description: 'Chart label for the specific job offer salary with role level, where {roleLevel} is the level' },
            { roleLevel: jobRoleLevel }
          )
        : intl.formatMessage({ id: 'salaryChart.specificOfferNoLevel', defaultMessage: 'This Offer', description: 'Chart label for the specific job offer salary when role level is not available' });
      
      ranges.push({
        stats: jobStats,
        label: offerLabel,
        color: '#10b981', // Green
        type: 'job',
      });
    } else if (ranges.length > 0) {
      // If no job salary, add an estimate at the end
      // Use the most specific range available to estimate
      const mostSpecific = ranges[ranges.length - 1];
      const p25Min = mostSpecific.stats.percentile25?.min || mostSpecific.stats.median.min * 0.85;
      const p75Max = mostSpecific.stats.percentile75?.max || mostSpecific.stats.median.max * 1.15;
      const estimatedMid = (p25Min + p75Max) / 2;
      
      const estimatedStats: SalaryStatistics = {
        median: { min: estimatedMid, max: estimatedMid },
        average: { min: p25Min, max: p75Max },
        min: p25Min,
        max: p75Max,
        percentile25: { min: p25Min, max: estimatedMid },
        percentile75: { min: estimatedMid, max: p75Max },
        sampleSize: mostSpecific.stats.sampleSize,
      };
      
      ranges.push({
        stats: estimatedStats,
        label: intl.formatMessage({ id: 'salaryChart.estimatedOffer', defaultMessage: 'Estimated Offer', description: 'Chart label for estimated salary when actual job salary is not provided' }),
        color: '#10b981', // Green
        type: 'job',
      });
    }

    return ranges;
  };

  const salaryRanges = collectSalaryRanges();


  // If no ranges, don't render
  if (salaryRanges.length === 0) {
    return null;
  }

  // Calculate overall chart range from all ranges (include 0 so scale matches bar positions)
  let chartMin = Infinity;
  let chartMax = -Infinity;

  for (const r of salaryRanges) {
    if (isValidNumber(r.stats.min) && r.stats.min < chartMin) {
      chartMin = r.stats.min;
    }
    if (isValidNumber(r.stats.max) && r.stats.max > chartMax) {
      chartMax = r.stats.max;
    }
  }

  if (chartMin === Infinity || chartMax === -Infinity || chartMax <= chartMin) {
    return null;
  }

  const range = chartMax - chartMin;

  // Format currency - always use EUR
  const formatCurrency = (value: number): string => {
    if (!isValidNumber(value) || value <= 0) return displayCurrency + '0';
    const rounded = Math.round(value);
    return `${displayCurrency}${rounded.toLocaleString(locale)}`;
  };

  const formatPeriod = (period: string): string => {
    switch (period) {
      case 'annual': return intl.formatMessage({ id: 'salaryStats.perYear', defaultMessage: 'per year' });
      case 'monthly': return intl.formatMessage({ id: 'salaryStats.perMonth', defaultMessage: 'per month' });
      case 'daily': return intl.formatMessage({ id: 'salaryStats.perDay', defaultMessage: 'per day' });
      case 'hourly': return intl.formatMessage({ id: 'salaryStats.perHour', defaultMessage: 'per hour' });
      default: return '';
    }
  };

  // Calculate median midpoint
  const getMedianMid = (stats: SalaryStatistics): number => {
    const medianMin = stats.median?.min || 0;
    const medianMax = stats.median?.max || 0;
    if (medianMin > 0 && medianMax > 0) {
      return (medianMin + medianMax) / 2;
    } else if (medianMin > 0) {
      return medianMin;
    } else if (medianMax > 0) {
      return medianMax;
    }
    return (stats.min + stats.max) / 2;
  };

  return (
    <div className={`salary-distribution-chart ${className} mt-4 min-w-0`}>
      <div className="mb-6">
        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
          <FormattedMessage 
            id="salaryChart.salaryContext" 
            defaultMessage="Salary Context: {title}"
            description="Chart context label explaining what job title the salary data is based on, where {title} is the job title" 
            values={{ title: jobTitle || intl.formatMessage({ id: 'salaryChart.thisRole', defaultMessage: 'this role', description: 'Fallback text when job title is not available, used in salary chart labels' }) }}
          />
        </div>
      </div>

      {/* Chart - Compact vertical bars (all screen sizes, space-efficient) */}
      <div className="bg-white dark:bg-gray-800 p-5 rounded-3xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div className="space-y-4 px-3 md:px-4">
          {salaryRanges.map((salaryRange, index) => {
            const medianMid = getMedianMid(salaryRange.stats);
            const min = salaryRange.stats.min;
            const max = salaryRange.stats.max;
            const range = chartMax - chartMin;
            const barWidthPercent = ((max - min) / range) * 100;
            const medianPercent = ((medianMid - chartMin) / range) * 100;

            return (
              <div key={index} className="relative">
                {/* Label */}
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  {salaryRange.label}
                </div>
                
                {/* Bar container */}
                <div className="relative h-8 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden">
                  {/* Bar */}
                  <div
                    className="h-full rounded-lg opacity-90"
                    style={{
                      width: `${barWidthPercent}%`,
                      backgroundColor: salaryRange.color,
                      marginLeft: `${((min - chartMin) / range) * 100}%`,
                    }}
                  />
                  
                  {/* Median marker */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white"
                    style={{
                      left: `${medianPercent}%`,
                      backgroundColor: salaryRange.color,
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                  {/* Range text on top of bar – min left, max right, gap at median dot; pills inset from bar edges */}
                  <div className="absolute inset-0 flex items-center pointer-events-none mx-2">
                    <span
                      className="text-xs font-medium text-white truncate min-w-0 rounded px-1.5 py-0.5 bg-black/50"
                      style={{ maxWidth: `calc(${medianPercent}% - 10px)` }}
                    >
                      {formatCurrency(min)}
                    </span>
                    <span
                      className="shrink-0 w-5 h-5"
                      style={{ position: 'absolute', left: `${medianPercent}%`, transform: 'translateX(-50%)' }}
                      aria-hidden
                    />
                    <span
                      className="text-xs font-medium text-white truncate min-w-0 text-right rounded px-1.5 py-0.5 bg-black/50 absolute right-0"
                      style={{ maxWidth: `calc(${100 - medianPercent}% - 10px)` }}
                    >
                      {formatCurrency(max)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Scale */}
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 px-3 md:px-4">
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>{formatCurrency(chartMin)}</span>
            <span>{formatCurrency(chartMax)}</span>
          </div>
          <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
            {formatPeriod('monthly')}
          </div>
        </div>
      </div>
    </div>
  );
});

SalaryDistributionChart.displayName = 'SalaryDistributionChart';
